import SwapConfig from '@components/SwapConfig'
import { PriceDifference } from '@plug-core/hooks/useSwap'
import useSwapReview from '@plug-core/hooks/useSwapReview'
import { SwapProviderQuote } from '@plug-core/interfaces/swapInterface'
import { useSwapContext } from '@plug-core/providers/SwapProvider'
import { Button, Dialog, IconButton, ListGroup, ListRow, Text } from '@ui'
import React, { FC, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { HiInformationCircle } from 'react-icons/hi'
import { VscSettings } from 'react-icons/vsc'
import ExchangeReview from './ExchangeReview'

interface Props {
  performSwap: (quote: SwapProviderQuote) => Promise<number>
  currentStep?: string
  onError: (error: string) => void
  /** Called as soon as the swap lands; the parent closes the sheet and leaves. */
  onSuccess: () => void
  formattedFee?: string[]
  priceDifference?: PriceDifference
}

const SwapReview: FC<Props> = ({
  performSwap,
  currentStep,
  formattedFee,
  onError,
  onSuccess,
  priceDifference,
}) => {
  const { t } = useTranslation()
  const { fromToken, toToken, toAmount, amount, swapConfigVisible, setSwapConfigVisible } =
    useSwapContext()
  const { userApprovedQuote, swapLoading, swapSuccess, swapDetails, doSwap } = useSwapReview({
    performSwap,
    onError,
    // The hook closes 3s after success (it used to show a success animation meanwhile). Leaving
    // is handled right away by onSuccess, so only its balance refresh still matters.
    close: () => {},
    formattedFee,
    priceDifference,
  })

  const [infoPromptText, setInfoPromptText] = useState<string>()
  const [showPriceDifferenceWarning, setShowPriceDifferenceWarning] = useState(false)

  const buttonTitle = useMemo(() => {
    if (!swapLoading) return t('swap.title')

    return t(`swap.steps.${currentStep}`)
  }, [swapLoading, currentStep, t])

  useEffect(() => {
    if (swapSuccess) onSuccess()
  }, [swapSuccess])

  return (
    <ExchangeReview
      fromTokenImage={fromToken!.logo}
      toTokenImage={toToken!.logo}
      fromTokenSymbol={fromToken!.symbol}
      toTokenSymbol={toToken!.symbol}
      fromTokenChain={fromToken?.chain}
      toTokenChain={toToken?.chain}
      // Stays busy while the sheet slides out after success.
      loading={swapLoading || swapSuccess}
      toAmount={swapLoading ? userApprovedQuote!.priceHuman.toString() : toAmount}
      amount={amount}
      handleSubmit={() => {
        if (priceDifference?.warn) {
          setShowPriceDifferenceWarning(true)
        } else {
          doSwap()
        }
      }}
      buttonTitle={buttonTitle}
      kind="swap"
    >
      <ListGroup className="mt-2">
        {swapDetails.map(({ title, value, adjustable, info }, index) => (
          <ListRow
            key={index}
            dense
            primary={<span className="text-[14px] font-normal">{title}</span>}
            trailing={
              <>
                <Text as="span" variant="body-s" tone="fg-2" tabular>
                  {value}
                </Text>
                {(adjustable && !swapLoading) || info ? (
                  <span className="-mr-2 flex">
                    {adjustable && !swapLoading && (
                      <IconButton
                        aria-label={t('swap.config')}
                        size="sm"
                        className="text-accent"
                        onClick={() => setSwapConfigVisible(true)}
                      >
                        <VscSettings />
                      </IconButton>
                    )}

                    {info && (
                      <IconButton
                        aria-label={title}
                        size="sm"
                        className="text-accent"
                        onClick={() => setInfoPromptText(info)}
                      >
                        <HiInformationCircle />
                      </IconButton>
                    )}
                  </span>
                ) : (
                  <span aria-hidden="true" className="w-6" />
                )}
              </>
            }
          />
        ))}
      </ListGroup>

      <SwapConfig visible={swapConfigVisible} close={() => setSwapConfigVisible(false)} />

      <Dialog
        open={!!infoPromptText}
        onClose={() => setInfoPromptText(undefined)}
        description={infoPromptText}
        actions={
          <Button onClick={() => setInfoPromptText(undefined)} variant="secondary" fullWidth>
            {t('common.understood')}
          </Button>
        }
      />

      <Dialog
        open={showPriceDifferenceWarning}
        onClose={() => setShowPriceDifferenceWarning(false)}
        title={t('swap.priceDifferenceWarning.title')}
        description={t('swap.priceDifferenceWarning.description')}
        actions={
          <>
            <Button
              fullWidth
              onClick={() => {
                setShowPriceDifferenceWarning(false)
                doSwap()
              }}
              variant="primary"
            >
              {t('swap.priceDifferenceWarning.confirm')}
            </Button>

            <Button
              fullWidth
              onClick={() => setShowPriceDifferenceWarning(false)}
              variant="secondary"
            >
              {t('common.cancel')}
            </Button>
          </>
        }
      />
    </ExchangeReview>
  )
}

export default SwapReview
